<template>
  <Toolbar class="mb-8 p-2 py-1">
    <template #start>
      <Paginator
        :first="(currentPage - 1) * pageSize"
        :rows="pageSize"
        :totalRecords="totalRecords"
        :rowsPerPageOptions="rowsPerPageOptions"
        @page="onPaginatorChange"
      />
    </template>
    <template #end>
      <slot name="custom-primaryActions">
        <ButtonGroup>
          <Button
            v-if="props.showRowActions"
            icon="pi pi-plus"
            :label="t('labels.add')"
            :disabled="!profileStore.userProfile?.isSeyboldUser"
            @click="handleCreateClick"
          />
          <Button
            v-if="props.isDownloadAvailable"
            severity="success"
            icon="pi pi-download"
            class="px-4"
            @click="csvDialogVisible = true"
          />
        </ButtonGroup>
      </slot>
    </template>
  </Toolbar>

  <Toolbar class="secondaryToolbar mb-2 p-0">
    <template #start>
      <PaginatedDataTablePrefilterTabs
        v-if="props.prefilterTabs"
        :tabs="props.prefilterTabs"
        :data-key="dataKey"
        @change="onPrefilterChange"
      />
      <slot
        name="custom-secondaryActions"
        :selectedRows="selectedRows"
        :confirmDeleteSelected="confirmDeleteSelected"
      >
        <Button
          v-if="props.onDelete && selectedRows.length"
          icon="pi pi-trash"
          :label="t('labels.deleteMany', { count: selectedRows.length })"
          severity="danger"
          :disabled="!profileStore.userProfile?.isSeyboldUser"
          @click="confirmDeleteSelected"
      /></slot>
    </template>
    <template #center></template>
    <template #end>
      <Button
        v-tooltip="{
          value: t('titletags.toggleRefresh'),
          fitContent: true,
          showDelay: 300,
        }"
        icon="mdi mdi-refresh"
        class="p-button-text settings-button ml-2 -mr-4"
        aria-label="Refresh"
        @click="() => fetchDataWithPagination()"
      />
      <Button
        v-if="props.showSplitView"
        v-tooltip="{
          value: t(isSplitView ? 'titletags.toggleSplitviewOn' : 'titletags.toggleSplitviewOff'),
          fitContent: true,
          showDelay: 300,
        }"
        :icon="isSplitView ? 'mdi mdi-fullscreen' : 'mdi mdi-arrow-split-vertical'"
        class="p-button-text settings-button ml-2 -mr-4"
        aria-label="Split View"
        @click="onToggleSplitView"
      />
      <Button
        v-tooltip="{
          value: t('titletags.showTableSettings'),
          fitContent: true,
          showDelay: 300,
        }"
        icon="pi pi-sliders-h"
        class="p-button-text settings-button mx-2"
        aria-label="Settings"
        @click="settingsDrawerVisible = true"
      />
      <PaginatedTableQuickFilter
        class="mx-0 w-full md:w-auto"
        :dataKey="dataKey"
        :filterString="savedFilterString"
        @update:filter-string="onFilterStringUpdate"
      />
    </template>
  </Toolbar>
  <DataTable
    v-model:expandedRows="rowsToExpand"
    v-model:selection="selectedRows"
    dataKey="id"
    reorderableColumns
    showGridlines
    lazy
    :value="data"
    :size="size"
    :columns="visibleColumns"
    :totalRecords="totalRecords"
    :multiSortMeta="currentSortMeta"
    sortMode="multiple"
    :removableSort="true"
    :rowClass="() => 'elevated-row'"
    @sort="(e) => onSortChange(e)"
    @rowClick="onRowClick"
    @rowDblclick="onRowDblClick"
  >
    <Column
      v-if="checkboxMultiselect"
      selectionMode="multiple"
      headerStyle="width: 3rem"
      :class="[`table-cell`]"
      :reorderableColumn="false"
    />
    <Column
      v-if="hasExpandedRow && collapsedExpandedRow"
      expander
      style="width: 3rem"
      :class="[`table-cell`]"
      :reorderableColumn="false"
    />
    <!-- Columns that are visible based on user settings and default configuration -->
    <Column
      v-for="(col, colIndex) in visibleColumns"
      :key="colIndex"
      :field="col.field"
      :sortable="col?.sortable"
    >
      <template v-if="col" #header>
        <slot name="custom-header" :column="col" :columnIndex="colIndex">
          <div
            v-tooltip="{ value: col.title, fitContent: true, showDelay: 300 }"
            class="uppercase"
            :data-testid="`datatable-header-${dataKey}-${col.field}`"
            :data-field="col.field"
          >
            <span>{{ getResponsiveHeaderLabel(col, t) }}</span>
          </div>
        </slot>
      </template>

      <template #body="slotProps">
        <PaginatedDataTableCell
          :cell="slotProps.data[col.field]"
          :col="col"
          :rowData="slotProps.data"
          :rowIndex="slotProps.index"
          @toggleTruncation="onToggleTruncation"
        >
          <template #display>
            <slot
              :name="`body-${col.field}`"
              :row="slotProps.data"
              :column="col"
              :data-key="dataKey"
              :field="col.field"
              :rowIndex="slotProps.index"
              :onToggleTruncation="onToggleTruncation"
            >
              <component
                :is="TemplateComponents[getEffectiveTemplate(col)!]"
                v-if="getEffectiveTemplate(col)"
                :row="slotProps.data"
                :column="col"
                :cell="slotProps.data[col.field]"
                :data-key="dataKey"
              />
            </slot>
          </template>
        </PaginatedDataTableCell>
      </template>
    </Column>

    <template v-if="hasExpandedRow" #expansion="slotProps">
      <slot name="custom-expanded-row" :row="slotProps.data">
        Add your expanded content at the usage site via the "custom-expanded-row" slot
      </slot>
    </template>
  </DataTable>

  <Confirm ref="confirmDialog" />

  <PaginatedTableSettingsDrawer
    v-model:visible="settingsDrawerVisible"
    :columns="columns"
    :visibleColumns="visibleColumns.map((col) => col.field)"
    :data-key="dataKey"
    @update:visible-columns="onVisibleColumnsChange"
    @update:size="onSizeUpdate"
  />
  <CSVExport v-model:visible="csvDialogVisible" :dataKey="dataKey">
    <template #default="{ dataKey: exportDataKey }">
      <component
        :is="props.exportComponent"
        v-if="props.exportComponent"
        :dataKey="exportDataKey"
        @cancel="csvDialogVisible = false"
        @export="handleExport"
      />
    </template>
  </CSVExport>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { ButtonGroup } from 'primevue';
import Column from 'primevue/column';
import DataTable, {
  type DataTableRowClickEvent,
  type DataTableRowDoubleClickEvent,
  type DataTableSortEvent,
  type DataTableSortMeta,
} from 'primevue/datatable';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import Confirm from '@/components/confirm/Confirm.vue';
import { useProfileStore } from '@/stores/profile.ts';
import { useSplitViewStore } from '@/stores/splitview';
import { useTableFilterStateStore } from '@/stores/tableFilter';
import { useTablePageSizeStore } from '@/stores/tablePagesize';
import { useSizeStore } from '@/stores/tableSize';
import { type SortMeta, useTableSortStateStore } from '@/stores/tableSort';
import { useTableVisibleColumnsStore } from '@/stores/tableVisibleColumns';
import type { OrderByPropertyDto } from '@/openapi/index.ts';

import PaginatedDataTableCell from './components/PaginatedDataTableCell.vue';
import PaginatedDataTablePrefilterTabs, {
  type PaginatedPrefilterOption,
} from './components/PaginatedDataTablePrefilterTabs.vue';
import PaginatedTableQuickFilter from './components/PaginatedTableQuickFilter.vue';
import PaginatedTableSettingsDrawer from './components/PaginatedTableSettingsDrawer.vue';
import {
  convertColumnConfigToPaginatedDataTableColumnConfig,
  getResponsiveHeaderLabel,
  rowsPerPageOptions,
} from './configs/pageinatedDataTableConfig';
import { TemplateComponents } from './configs/paginatedDataTableTemplates';
import { getEffectiveTemplate } from './configs/paginatedDataTableUtils';
import { getFilteredAndSortedColumns } from './configs/paginatedDataTableUtils';
import CSVExport from './csv-export/CSVExport.vue';
import type {
  PaginatedDataTableColumnConfig,
  PaginatedDataTableProps,
} from './types/paginatedDataTableTypes';

const props = withDefaults(defineProps<PaginatedDataTableProps<Record<string, unknown>>>(), {
  collapsedExpandedRow: true,
  hasExpandedRow: false,
  checkboxMultiselect: false,
  showRowActions: true,
  showSplitView: true,
  isDownloadAvailable: false,
});

const emit = defineEmits<{
  (e: 'update:selected-rows', rows: Record<string, unknown>[]): void;
}>();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const confirmDialog = ref<InstanceType<typeof Confirm> | null>(null);
const csvDialogVisible = ref(false);

//Stores
const visibleColumnsStore = useTableVisibleColumnsStore();
const sizeStore = useSizeStore();
const splitViewStore = useSplitViewStore();
const tableSortStateStore = useTableSortStateStore();
const tableFilterStateStore = useTableFilterStateStore();
const tablePageSizeStore = useTablePageSizeStore();
const profileStore = useProfileStore();

type DataRef<T> = {
  id: string;
  value: T;
  _meta?: { hasExpandableData: boolean };
} & Record<string, unknown>;

const data = ref<DataRef<Record<string, unknown>>[]>([]);

//Pagination states
const totalRecords = ref(0);
const currentPage = ref(1);
const pageSize = ref(tablePageSizeStore.pageSize);

//Drawer states
const settingsDrawerVisible = ref(false);

// Row expansion state
const rowsToExpand = ref<Record<string, boolean>>({});

// Row selection state
const selectedRows = ref<Record<string, unknown>[]>([]);

// Watch selection and emit changes
watch(selectedRows, (newValue) => {
  emit('update:selected-rows', newValue);
});

// Routing
const isSplitView = ref(false);

// Click delay for double-click detection
const clickTimer = ref<ReturnType<typeof setTimeout> | null>(null);

//Table states
const size = ref(sizeStore.size || 'normal');
const currentSortMeta = ref<DataTableSortMeta[]>([]);
const savedFilterString = ref<string>('');
const currentPrefilter = ref<PaginatedPrefilterOption | null>(null);

//computed values
const convertedColumns = computed<PaginatedDataTableColumnConfig[]>(() => {
  return convertColumnConfigToPaginatedDataTableColumnConfig(props.columns, t);
});

const visibleColumns = computed<PaginatedDataTableColumnConfig[]>(() => {
  return getFilteredAndSortedColumns(
    convertedColumns.value,
    visibleColumnsStore.getVisibleColumns(props.dataKey),
  );
});

const updatePageSize = (newPageSize: number) => {
  if (newPageSize === pageSize.value) return;
  pageSize.value = newPageSize;
  tablePageSizeStore.setPageSize(newPageSize);
  currentPage.value = 1; // Back to first page, if pagesize changed
};

// Enrich data with truncation: store original value, show truncated by default
const enrichDataWithTruncation = <T extends Record<string, unknown> = Record<string, unknown>>(
  rowData?: T[],
): DataRef<T>[] => {
  return (
    rowData?.map((row) => {
      const newRow = { ...row } as Record<string, unknown>;
      props.columns.forEach((col) => {
        const value = row[col.field];
        if (col.truncateLength && typeof value === 'string' && value.length > col.truncateLength) {
          const truncatedValue = value.slice(0, col.truncateLength) + '...';

          newRow[`original_${col.field}`] = value;
          newRow[col.field] = truncatedValue;
        }
      });

      return newRow as DataRef<T>;
    }) ?? []
  );
};

// Construct Prisma-style filter from active prefilter option
const constructPreFilterForFetchData = () => {
  if (!currentPrefilter.value?.focusedColumn) {
    return undefined;
  }

  // Build filter: { [focusedColumn]: { equals: key } }
  return {
    [currentPrefilter.value.focusedColumn]: {
      equals: currentPrefilter.value.key,
    },
  };
};

const onSortChange = (e: DataTableSortEvent) => {
  if (!e.multiSortMeta) return;

  currentSortMeta.value = e.multiSortMeta;
  tableSortStateStore.setSortOrder(props.dataKey, e.multiSortMeta as SortMeta[]);
  currentPage.value = 1; // Reset to first page when sorting changes
};

const onFilterStringUpdate = (filterString: string) => {
  if (props.dataKey) {
    savedFilterString.value = filterString;
    currentPage.value = 1; // Reset to first page when filter changes
    tableFilterStateStore.setFilterString(props.dataKey, filterString);
  }
};

const fetchDataWithPagination = async () => {
  const filter = constructPreFilterForFetchData();

  const orderBy: OrderByPropertyDto[] | undefined = currentSortMeta.value.length
    ? currentSortMeta.value
        .map(
          (meta) =>
            (typeof meta.field === 'string' && {
              propertyName: meta.field,
              value: meta.order === 1 ? 'asc' : 'desc',
            }) ??
            undefined,
        )
        .filter((value): value is OrderByPropertyDto => value !== undefined && value !== false)
    : undefined;

  const response = await props.fetchData({
    page: currentPage.value,
    limit: pageSize.value,
    orderBy,
    searchString: savedFilterString.value,
    filter,
  });

  if (response) {
    // Ensure fresh array reference for Vue reactivity with lazy mode
    data.value = [...(enrichDataWithTruncation(response.data) ?? [])];
    totalRecords.value = response.count ?? 0;
  }
};

const onVisibleColumnsChange = (newVisibleColumns: string[]) => {
  visibleColumnsStore.setVisibleColumns(props.dataKey, newVisibleColumns);
};

const onSizeUpdate = (newSize: string) => {
  size.value = newSize;
};

const onPaginatorChange = (event: { page: number; rows: number }) => {
  currentPage.value = event.page + 1; // Paginator is 0-indexed, convert to 1-indexed
  updatePageSize(event.rows); // setting pageSize + currentPage
};

const onToggleTruncation = (rowIndex: number, fieldName: string) => {
  const row = data.value[rowIndex];
  if (!row) return;

  const originalKey = `original_${fieldName}`;
  const hasOriginal = originalKey in row;

  if (hasOriginal) {
    // Swap between truncated and original
    const current = row[fieldName];
    row[fieldName] = row[originalKey];
    row[originalKey] = current;
  }
};

const onToggleSplitView = () => {
  const pathChunks = route.path.split('/').filter(Boolean);
  const isDetailView = pathChunks.length >= 2; // e.g. /contacts/someuid

  splitViewStore.toggleViewMode();
  isSplitView.value = !isSplitView.value;

  if (isDetailView && !splitViewStore.isSplitView) {
    window.location.reload(); // reload to show full detail view
  }
};

const clearTimer = () => {
  if (clickTimer.value) {
    clearTimeout(clickTimer.value);
    clickTimer.value = null;
  }
};

const onRowClick = (event: DataTableRowClickEvent) => {
  clearTimer();

  // Delay single-click action to detect potential double-click
  clickTimer.value = setTimeout(() => {
    if (props.onRowClick) {
      props.onRowClick(event);
      return;
    }
    const comp = Array.isArray(route.params.component)
      ? route.params.component[0]
      : route.params.component;
    void router.push(`/${comp}/${event.data.id}`);
  }, 250); // 250ms delay to detect double-click
};

const onRowDblClick = async (event: DataTableRowDoubleClickEvent) => {
  clearTimer();

  if (props.onRowDblClick) {
    props.onRowDblClick(event);
    return;
  }
  const comp = Array.isArray(route.params.component)
    ? route.params.component[0]
    : route.params.component;
  await router.push(`/${comp}/${event.data.id}/edit`);
};

const onPrefilterChange = (option: PaginatedPrefilterOption) => {
  currentPrefilter.value = option;
  currentPage.value = 1; // Reset to first page when prefilter changes
};

const handleExpandedRows = () => {
  if (!props.collapsedExpandedRow && props.hasExpandedRow) {
    const map: Record<string, boolean> = {};
    for (const row of data.value) {
      const id = row.id;

      const metaValue = row?._meta;
      const allow = !metaValue || metaValue.hasExpandableData;
      if (id != null && allow) map[String(id)] = true;
    }
    rowsToExpand.value = map;
  }
};

async function handleCreateClick() {
  if (props.onCreate) {
    await props.onCreate();
    return;
  }
  const comp = Array.isArray(route.params.component)
    ? route.params.component[0]
    : route.params.component;
  void router.push(`/${comp}/new`);
}

function handleExport(fields: string[]) {
  // Export logic is handled by the custom export component
  // This handler can be extended in the future if needed
  console.log('Export requested with fields:', fields);
  csvDialogVisible.value = false;
}

function deleteSelected() {
  if (!props.onDelete) return;

  const ids = selectedRows.value.map((row: any) => row.id);

  void props.onDelete(ids).then(async () => {
    await fetchDataWithPagination();
    selectedRows.value = [];
  });
}

async function confirmDeleteSelected() {
  if (!confirmDialog.value) return;

  const isConfirmed = await confirmDialog.value.isDeleteConfirmed(
    props.confirmationMessage ? props.confirmationMessage(selectedRows.value) : undefined,
  );

  if (isConfirmed) {
    deleteSelected();
  }
}

const loadInitialSortOrder = () => {
  if (props.dataKey) {
    const initialSortMeta = tableSortStateStore.getSortOrder(props.dataKey);

    if (initialSortMeta && initialSortMeta.length > 0) {
      currentSortMeta.value = initialSortMeta.map((meta) => ({
        field: meta.field,
        order: meta.order as 1 | -1 | 0,
      }));
    }
  }
};

async function bootstrapTable() {
  await fetchDataWithPagination();
  handleExpandedRows();

  await nextTick();
  loadInitialSortOrder();
  savedFilterString.value = tableFilterStateStore.getFilterString(props.dataKey);
}

watch([currentPage, pageSize, currentSortMeta, savedFilterString, currentPrefilter], async () => {
  await fetchDataWithPagination();
});

onMounted(async () => {
  await bootstrapTable();
});
</script>

<style lang="css">
.p-datatable-column-header-content {
  justify-content: center;
}

.elevated-row td {
  border-top: 0.6em solid var(--surface-ground) !important;
}
</style>
